import type { PaletteOptions } from "@mui/material/styles";
import {
  brand,
  danger,
  darkSurface,
  darkText,
  info,
  neutral,
  success,
  warning,
} from "./tokens";

export const lightPalette: PaletteOptions = {
  mode: "light",
  primary: {
    ...brand,
    light: brand[400],
    main: brand[600],
    dark: brand[800],
    contrastText: "#ffffff",
  },
  secondary: {
    light: neutral[400],
    main: neutral[600],
    dark: neutral[800],
    contrastText: "#ffffff",
  },
  success: {
    light: success.light,
    main: success.main,
    dark: success.dark,
    contrastText: success.contrastText,
  },
  warning: {
    light: warning.light,
    main: warning.main,
    dark: warning.dark,
    contrastText: warning.contrastText,
  },
  error: {
    light: danger.light,
    main: danger.main,
    dark: danger.dark,
    contrastText: danger.contrastText,
  },
  info: {
    light: info.light,
    main: info.main,
    dark: info.dark,
    contrastText: info.contrastText,
  },
  grey: neutral,
  divider: neutral[200],
  background: {
    default: neutral[100],
    paper: "#ffffff",
  },
  text: {
    primary: neutral[900],
    secondary: neutral[600],
    disabled: neutral[400],
  },
  action: {
    hover: "rgba(63, 99, 133, 0.06)",
    selected: "rgba(63, 99, 133, 0.10)",
    focus: "rgba(63, 99, 133, 0.16)",
  },
  // Consumed by @mui/x-data-grid to drive its own CSS variables; without these
  // it derives everything from `background.paper` and the header disappears
  // into the body.
  DataGrid: {
    bg: "#ffffff",
    headerBg: neutral[100],
    pinnedBg: "#ffffff",
  },
};

export const darkPalette: PaletteOptions = {
  mode: "dark",
  primary: {
    ...brand,
    // `main` is deliberately a mid blue rather than the pale `brand[300]` this
    // used to be: it is what contained buttons fill with, and only a mid blue
    // clears 4.5:1 against white button text (brand[400] manages just 3.3:1).
    // `light` is the tone for anything rendered *as* text on a dark surface --
    // links, icons, selected tabs -- where `main` would only reach 3.5:1.
    light: brand[300],
    main: brand[500],
    dark: brand[600],
    contrastText: "#ffffff",
  },
  secondary: {
    light: neutral[200],
    main: neutral[400],
    dark: neutral[600],
    contrastText: darkSurface.canvas,
  },
  success: {
    light: "#a5d6a7",
    main: success.darkMode,
    dark: success.main,
    contrastText: darkSurface.canvas,
  },
  warning: {
    light: "#fcd34d",
    main: warning.darkMode,
    dark: warning.main,
    contrastText: darkSurface.canvas,
  },
  error: {
    light: "#f6a49e",
    main: danger.darkMode,
    dark: danger.main,
    contrastText: darkSurface.canvas,
  },
  info: {
    light: "#81d4fa",
    main: info.darkMode,
    dark: info.main,
    contrastText: darkSurface.canvas,
  },
  grey: neutral,
  // A solid border rather than a 12% white wash, which left card and grid
  // outlines all but invisible.
  divider: darkSurface.border,
  background: {
    default: darkSurface.canvas,
    paper: darkSurface.paper,
  },
  text: {
    primary: darkText.primary,
    secondary: darkText.secondary,
    disabled: darkText.disabled,
  },
  // Neutral rather than blue-tinted, so hovering a table row reads as a lift
  // instead of a colour change.
  action: {
    hover: "rgba(255, 255, 255, 0.06)",
    selected: "rgba(255, 255, 255, 0.10)",
    focus: "rgba(255, 255, 255, 0.14)",
  },
  DataGrid: {
    bg: darkSurface.paper,
    // Lighter than the body it sits on. The previous header used
    // `background.default`, i.e. darker than its own card, which read as a hole.
    headerBg: darkSurface.raised,
    pinnedBg: darkSurface.raised,
  },
};
